import { useCallback, useEffect, useRef, useState } from "react";
import { subscribeDataChanges } from "./dataChanges";
import {useVisibleRefresh} from "./useVisibleRefresh";
import {readAccessLost} from "./userError";

export const useDataRefresh = (refresh, resources = []) => {
  const latest = useRef(refresh);
  latest.current = refresh;
  const resourceKey = JSON.stringify(resources);
  useEffect(() => subscribeDataChanges(JSON.parse(resourceKey), () => latest.current()), [resourceKey]);
};

// Minimal fetch hook with loading/error/refetch.
export const useData = (fetcher, deps = [], resources = [], {autoRefreshMs=0}={}) => {
  const key = JSON.stringify(deps);
  const currentKey = useRef(key);
  currentKey.current = key;
  const generation = useRef(0);
  const [state, setState] = useState({ key, data: null, busy: true, error: null, updatedAt: null });

  const load = useCallback(async () => {
    const request = ++generation.current;
    const requestKey = key;
    setState((previous) => ({ ...previous, key: requestKey, data: previous.key === requestKey ? previous.data : null, busy: true, error: null }));
    try {
      const res = await fetcher();
      if (generation.current !== request || currentKey.current !== requestKey) return;
      setState({ key: requestKey, data: res, busy: false, error: null, updatedAt: new Date() });
      return res;
    } catch (e) {
      if (generation.current === request && currentKey.current === requestKey) setState((previous) => ({ ...previous, ...(readAccessLost(e)?{data:null,updatedAt:null}:{}), busy: false, error: e }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  useDataRefresh(load, resources);

  const sameQuery = state.key === key;
  const data = sameQuery ? state.data : null;
  useVisibleRefresh(load,{enabled:autoRefreshMs>0&&sameQuery&&!state.busy&&!readAccessLost(state.error),intervalMs:autoRefreshMs,updatedAt:sameQuery?state.updatedAt:null});
  return { data, loading: !sameQuery || (state.busy && data === null), refreshing: sameQuery && state.busy && data !== null,
    error: sameQuery ? state.error : null, updatedAt: sameQuery ? state.updatedAt : null, refetch: load,
    setData: (value) => setState((previous) => ({ ...previous, data: typeof value === "function" ? value(previous.data) : value })),
  };
};
